import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { CheckCircleFill, XCircleFill } from 'react-bootstrap-icons'
import InputMask from 'react-input-mask'
import Swal from 'sweetalert2'
import { apiCep } from '../../../services/apiCep'
import apinode from '../../../services/apinode'
import removeNonCharacters from '../../../utils/removeNonCharacters'
import { CustomButton } from '../../CustomButton'
import { CustomSpinner } from '../../CustomSpinner'
import { Container, Footer } from './styles'

export const FormFuncionarios = forwardRef(
  ({ onSubmit, onClose, requestIsLoading, buttonSubmitText }, ref) => {
    const initialState = useMemo(
      () => ({
        nomeFuncionario: '',
        cpf: '',
        rg: '',
        cep: '',
        endereco: '',
        bairro: '',
        uf: '',
        cidade: '',
        numero: '',
        complemento: '',
        fonefixo: '',
        fonecelular: '',
        email: '',
        usuario: '',
        admin: 'NAO',
        id_conta: '',
        ativo: 'S',
      }),
      [],
    )

    const [formData, setFormData] = useState(initialState)
    const [errors, setErrors] = useState({})
    const [contas, setContas] = useState([])
    const [isCepLoading, setIsCepLoading] = useState(false)

    function handleChange(e) {
      const { name, value } = e.target

      setFormData({
        ...formData,
        [name]: value,
      })
    }

    async function handleCepChange(e) {
      const { value } = e.target

      const valueFormatted = removeNonCharacters(value)

      setFormData((prevState) => ({
        ...prevState,
        cep: value,
      }))

      if (valueFormatted.length === 8) {
        setIsCepLoading(true)

        try {
          const response = await apiCep.get(`/ws/${valueFormatted}/json/`)

          setFormData((prevState) => ({
            ...prevState,
            endereco: response.data.logradouro,
            bairro: response.data.bairro,
            cidade: response.data.localidade,
            uf: response.data.uf,
          }))
        } catch (err) {
          console.log(err)

          Swal.fire(
            'Erro',
            'CEP não localizado, preencha as informações manualmente!',
            'error',
          )
        } finally {
          setIsCepLoading(false)
        }
      }
    }

    const validateForm = () => {
      const newErrors = {}

      if (!formData.nomeFuncionario.trim()) {
        newErrors.nomeFuncionario = 'Nome do Funcionário é obrigatório!'
      }

      if (!formData.usuario.trim()) {
        newErrors.usuario = 'Usuário é obrigatório!'
      }

      setErrors(newErrors)

      return Object.keys(newErrors).length === 0
    }

    useImperativeHandle(
      ref,
      () => ({
        resetForm: () => {
          setFormData(initialState)
          setErrors({})
        },
        setFields: (funcionario) => {
          setFormData({
            id: funcionario.IDFUNCIONARIO || '',
            nomeFuncionario: funcionario.NOMEFUNCIONARIO || '',
            cpf: funcionario.CPF || '',
            rg: funcionario.RG || '',
            cep: funcionario.CEP || '',
            endereco: funcionario.ENDERECO || '',
            numero: funcionario.NUMERO || '',
            bairro: funcionario.BAIRRO || '',
            cidade: funcionario.CIDADE || '',
            uf: funcionario.ESTADO || '',
            complemento: funcionario.COMPLEMENTO || '',
            fonefixo: funcionario.FONEFIXO || '',
            fonecelular: funcionario.FONECELULAR || '',
            email: funcionario.EMAIL || '',
            usuario: funcionario.USUARIO || '',
            admin: funcionario.ADMIN || '',
            id_conta: funcionario.ID_CONTA || '',
            ultimoAcesso: funcionario.DATAULTIMOACESSO
              ? format(
                  new Date(funcionario.DATAULTIMOACESSO),
                  "d 'de' MMMM 'de' yyyy, 'ás' H:m",
                  { locale: ptBR },
                )
              : '',
            ativo: funcionario.ATIVO || '',
          })
        },
      }),
      [initialState],
    )

    async function handleSubmit(e) {
      e.preventDefault()

      if (validateForm()) {
        onSubmit(formData)
      }
    }

    async function loadAccounts() {
      try {
        const response = await apinode.get(
          `/pedido/contas/${localStorage.idconta}`,
        )

        setContas(response.data)
      } catch (error) {
        console.error('Erro ao buscar contas:', error)
      }
    }

    useEffect(() => {
      loadAccounts()
    }, [])

    return (
      <Container>
        <Form
          noValidate
          onSubmit={handleSubmit}
        >
          <Row xs={3}>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Nome do Funcionário *</Form.Label>
                <Form.Control
                  type="text"
                  name="nomeFuncionario"
                  value={formData.nomeFuncionario}
                  onChange={handleChange}
                  isInvalid={!!errors.nomeFuncionario}
                  isValid={formData.nomeFuncionario && !errors.nomeFuncionario}
                />

                <Form.Control.Feedback type="invalid">
                  {errors.nomeFuncionario}
                </Form.Control.Feedback>
                <Form.Control.Feedback type="valid">OK!</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group className="mb-3">
                <Form.Label>CPF</Form.Label>

                <InputMask
                  name="cpf"
                  mask="999.999.999-99"
                  onChange={handleChange}
                  value={formData.cpf}
                >
                  {(inputProps) => (
                    <Form.Control
                      type="text"
                      {...inputProps}
                    />
                  )}
                </InputMask>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group className="mb-3">
                <Form.Label>RG</Form.Label>

                <InputMask
                  name="rg"
                  mask="99.999.999-9"
                  onChange={handleChange}
                  value={formData.rg}
                >
                  {(inputProps) => (
                    <Form.Control
                      type="text"
                      {...inputProps}
                    />
                  )}
                </InputMask>
              </Form.Group>
            </Col>
          </Row>

          <Row xs={3}>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>CEP</Form.Label>
                <InputMask
                  name="cep"
                  mask="99.999-999"
                  onChange={handleCepChange}
                  value={formData.cep}
                >
                  {(inputProps) => (
                    <Form.Control
                      type="text"
                      {...inputProps}
                    />
                  )}
                </InputMask>
              </Form.Group>
              {isCepLoading && <CustomSpinner size={20} />}
            </Col>

            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Endereço</Form.Label>
                <Form.Control
                  name="endereco"
                  type="text"
                  value={formData.endereco}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Número</Form.Label>
                <Form.Control
                  name="numero"
                  type="number"
                  value={formData.numero}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row xs={4}>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Bairro</Form.Label>
                <Form.Control
                  value={formData.bairro}
                  name="bairro"
                  type="text"
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Cidade</Form.Label>
                <Form.Control
                  type="text"
                  name="cidade"
                  value={formData.cidade}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col>
              <Form.Group className="mb-3">
                <Form.Label>UF</Form.Label>
                <Form.Control
                  name="uf"
                  type="text"
                  value={formData.uf}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>

            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Complemento</Form.Label>
                <Form.Control
                  name="complemento"
                  value={formData.complemento}
                  type="text"
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row xs={3}>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Fone Fixo</Form.Label>
                <InputMask
                  name="fonefixo"
                  value={formData.fonefixo}
                  mask={'(99) 9999-9999'}
                  onChange={handleChange}
                >
                  {(inputProps) => (
                    <Form.Control
                      type="text"
                      {...inputProps}
                    />
                  )}
                </InputMask>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Celular</Form.Label>
                <InputMask
                  name="fonecelular"
                  value={formData.fonecelular}
                  mask="(99) 99999-9999"
                  onChange={handleChange}
                >
                  {(inputProps) => (
                    <Form.Control
                      type="text"
                      {...inputProps}
                    />
                  )}
                </InputMask>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group className="mb-3">
                <Form.Label>E-mail</Form.Label>
                <Form.Control
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row xs={2}>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Usuário Web *</Form.Label>
                <Form.Control
                  name="usuario"
                  type="text"
                  value={formData.usuario}
                  onChange={handleChange}
                  isInvalid={!!errors.usuario}
                  isValid={formData.usuario && !errors.usuario}
                />

                <Form.Control.Feedback type="invalid">
                  {errors.usuario}
                </Form.Control.Feedback>

                <Form.Control.Feedback type="valid">OK!</Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Último Acesso</Form.Label>
                <Form.Control
                  name="ultimoAcesso"
                  type="text"
                  disabled
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row xs={3}>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Usuário administrador</Form.Label>
                <Form.Select
                  value={formData.admin}
                  name="admin"
                  onChange={handleChange}
                >
                  <option value="SIM">Sim</option>
                  <option value="NAO">Não</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Conta do Usuário</Form.Label>
                <Form.Select
                  value={formData.id_conta}
                  name="id_conta"
                  onChange={handleChange}
                >
                  <option value="">Selecione uma conta</option>
                  {contas.map((conta) => (
                    <option
                      key={conta.ID_CONTA}
                      value={conta.ID_CONTA}
                    >
                      {conta.APELIDO}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Ativo</Form.Label>
                <Form.Select
                  value={formData.ativo}
                  name="ativo"
                  onChange={handleChange}
                >
                  <option value="">Selecione o status</option>
                  <option value="S">Sim</option>
                  <option value="N">Não</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Footer>
            <CustomButton
              title="Cancelar"
              onClick={onClose}
              isDisabled={requestIsLoading}
              color="#df0000"
            >
              <XCircleFill />
            </CustomButton>

            <CustomButton
              color="#059743"
              type="submit"
              title={buttonSubmitText}
              isLoading={requestIsLoading}
            >
              <CheckCircleFill />
            </CustomButton>
          </Footer>
        </Form>
      </Container>
    )
  },
)
